import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { logDebug } from "@/lib/log";

/**
 * Subscribe to a live-event stream.
 *
 * Returns a stateful bundle:
 *   • connected      — boolean (true once we've received the initial "hello")
 *   • submissions[]  — chronologically ordered (newest first)
 *   • lastSelection  — the most recent selection pushed to the display
 *   • mode           — "english" | "hebrew" (mirrors the current event mode)
 *   • event          — the full event doc (authoritative, see below)
 *   • applyEvent     — merge an event fetched over HTTP into that state
 *
 * Correctness never depends on an unbroken socket:
 *   1. Every server event carries a monotonic `seq`. We remember the last one
 *      seen and reconnect with `?since=`, so the server replays whatever we
 *      missed instead of it being lost forever.
 *   2. A poll of `/live/{slug}/state` every few seconds reconciles the event
 *      and current selection even if the stream is completely dead.
 *   3. `event` carries `rev`; an older copy never overwrites a newer one, so
 *      a late frame can't rewind the show (this is what made "Next" skip or
 *      no-op when the panel's position went stale).
 */
const RETRY_MS = [500, 1000, 2000, 4000, 6000];
// Server heartbeats every 5s. Give a cloud load balancer plenty of slack
// before declaring the socket dead — reconnects are cheap now (they replay),
// but thrashing them still costs delivery latency.
const STALE_MS = 25000;

export default function useLiveStream(slug, options = {}) {
  // The console and projector must never be wrong, so they reconcile often.
  // Audience phones are a crowd — they reconcile lazily to keep a full room
  // from hammering the server.
  const pollMs = options.pollMs || 4000;
  const [connected, setConnected] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [lastSelection, setLastSelection] = useState(null);
  const [mode, setMode] = useState("english");
  const [event, setEvent] = useState(null);
  // Bumped when the admin wipes rehearsal data, so phones can forget who
  // they were (their participant row is gone).
  const [resetAt, setResetAt] = useState(0);
  const esRef = useRef(null);
  const seqRef = useRef(0);
  const revRef = useRef(-1);
  const selectionAtRef = useRef(0);

  // Monotonic event merge — drops anything older than what we already hold.
  const applyEvent = useCallback((next) => {
    if (!next) return;
    const rev = typeof next.rev === "number" ? next.rev : 0;
    if (rev < revRef.current) return;
    revRef.current = rev;
    setEvent(next);
    if (next.mode) setMode(next.mode);
  }, []);

  const applySelection = useCallback((sel) => {
    if (!sel) return;
    const at = new Date(sel.selected_at || 0).getTime();
    // Strictly newer only — re-polling the same selection must not undo an
    // explicit clear (the display wipes the staff on some slide changes).
    if (at <= selectionAtRef.current) return;
    selectionAtRef.current = at;
    setLastSelection(sel);
  }, []);

  // Pages call this with a selection to merge, or `null` to clear the staff.
  const setSelection = useCallback((sel) => {
    if (sel === null) {
      setLastSelection(null);
      return;
    }
    applySelection(sel);
  }, [applySelection]);

  useEffect(() => {
    if (!slug) return undefined;
    const base = `${process.env.REACT_APP_BACKEND_URL}/api/live/${slug}`;
    let closed = false;
    let attempt = 0;
    let retryTimer = null;
    let lastFrameAt = Date.now();

    const handle = (msg) => {
      lastFrameAt = Date.now();
      let payload;
      try {
        payload = JSON.parse(msg.data);
      } catch {
        return;
      }
      if (typeof payload.seq === "number" && payload.seq > 0) {
        if (payload.seq <= seqRef.current && payload.type !== "hello") return;
        seqRef.current = Math.max(seqRef.current, payload.seq);
      }
      if (payload.type === "hello") {
        attempt = 0;
        setConnected(true);
        if (payload.mode) setMode(payload.mode);
      } else if (payload.type === "ping") {
        setConnected(true);
      } else if (payload.type === "submission") {
        setSubmissions((prev) =>
          prev.some((s) => s.id === payload.submission.id)
            ? prev
            : [payload.submission, ...prev]
        );
      } else if (payload.type === "submission_status") {
        setSubmissions((prev) =>
          prev.map((s) => (s.id === payload.id ? { ...s, status: payload.status } : s))
        );
      } else if (payload.type === "selection") {
        applySelection(payload.selection);
        // Auto-mark the source submission as selected in the local queue
        if (payload.selection.source === "audience") {
          setSubmissions((prev) =>
            prev.map((s) =>
              s.id === payload.selection.submission_id ? { ...s, status: "selected" } : s
            )
          );
        }
      } else if (payload.type === "audience_reset") {
        setSubmissions([]);
        setLastSelection(null);
        selectionAtRef.current = 0;
        setResetAt(Date.now());
      } else if (payload.type === "mode") {
        setMode(payload.mode);
      } else if (payload.type === "event_updated") {
        applyEvent(payload.event);
      }
    };

    const connect = () => {
      if (closed) return;
      const es = new EventSource(`${base}/stream?since=${seqRef.current}`);
      esRef.current = es;
      lastFrameAt = Date.now();
      es.onmessage = handle;
      es.onerror = () => {
        setConnected(false);
        es.close();
        if (closed) return;
        const wait = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];
        attempt += 1;
        retryTimer = window.setTimeout(connect, wait);
      };
    };

    connect();

    // Watchdog: a socket that stops delivering heartbeats is dead even if
    // the browser never fires `error`.
    const watchdog = window.setInterval(() => {
      if (closed) return;
      if (Date.now() - lastFrameAt > STALE_MS) {
        setConnected(false);
        esRef.current?.close();
        connect();
      }
    }, 4000);

    // Safety net: reconcile against the server even with the stream down.
    const poll = window.setInterval(async () => {
      if (closed) return;
      try {
        const { data } = await axios.get(`${base}/state`, { timeout: 6000 });
        applyEvent(data.event);
        if (data.selection) applySelection(data.selection);
      } catch (err) {
        // Offline for a beat — the next tick tries again.
        logDebug("live state poll failed", err);
      }
    }, pollMs);

    return () => {
      closed = true;
      window.clearInterval(watchdog);
      window.clearInterval(poll);
      if (retryTimer) window.clearTimeout(retryTimer);
      esRef.current?.close();
      esRef.current = null;
    };
  }, [slug, pollMs, applyEvent, applySelection]);

  return {    connected,
    submissions,
    setSubmissions,
    lastSelection,
    setLastSelection: setSelection,
    mode,
    setMode,
    event,
    applyEvent,
    resetAt,
  };
}
